import React, { useState } from 'react';
import {
  Container,
  Grid,
  Typography,
  Box,
  Card,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider
} from '@mui/material';
import { styled } from '@mui/material/styles';
import InfoIcon from '@mui/icons-material/Info';
import AssessmentIcon from '@mui/icons-material/Assessment';
import PieChartIcon from '@mui/icons-material/PieChart';
import PeopleIcon from '@mui/icons-material/People';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import LocalMall from '@mui/icons-material/LocalMall';
import { useData } from '../context/DataContext';

// Estilo personalizado para las tarjetas de información
const InfoCard = styled(Card)(({ theme }) => ({
  borderRadius: '8px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  padding: '10px',
  backgroundColor: '#F0F4F8',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'start',
  height: '100%',
  width: '100%',
  cursor: 'pointer',
  transition: 'transform 0.2s',
  '&:hover': {
    transform: 'scale(1.05)',
  },
}));

const MoreInfoButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(1),
  padding: '4px 8px',
  fontSize: '0.875rem',
  color: '#1F3D58',
  backgroundColor: '#E0E7EF',
  '&:hover': {
    backgroundColor: '#D0D8DF',
  },
}));

const DialogContentWrapper = styled(Box)({
  padding: '20px',
  fontSize: '1rem',
  color: '#333',
  lineHeight: '1.6',
});

const DialogSectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  fontSize: '1.2rem',
  marginBottom: theme.spacing(1),
  color: '#1F3D58',
}));

const InfoBoxes = () => {
  const [open, setOpen] = useState(false);
  const [dialogContent, setDialogContent] = useState({ title: '', content: '' });

  const { getDashboardStats, dataReady } = useData();

  // Headline stats pulled dynamically from the 2025 data (with fallbacks)
  const stats = dataReady ? getDashboardStats() : {};
  const topIndustry = stats.topIndustryLabel ?? 'Retail';
  const topShare = stats.topIndustryShare ?? 27;
  const fillRate = stats.fillRate ?? 96;

  // Join the top fill-rate sectors into a natural phrase: "a, b y c"
  const fillSectorList = (stats.fillRateSectors ?? ['Automotriz', 'Manufactura', 'Servicios logísticos', 'Retail'])
    .map((s) => s.toLowerCase());
  const fillSectors = fillSectorList.length > 1
    ? `${fillSectorList.slice(0, -1).join(', ')} y ${fillSectorList[fillSectorList.length - 1]}`
    : (fillSectorList[0] ?? '');
  const billingOver250 = stats.billingOver250 ?? 80;

  // Box 4: sector with the lowest personnel rotation (best retention)
  const rotationSector = stats.rotationSector ?? 'Automotriz';
  const rotationValue = stats.rotationValue ?? 31;

  // Box 5: sector with the highest perfect-order rate
  const perfectOrderSector = stats.perfectOrderSector ?? 'Sector salud/ Farmacéuticas';
  const perfectOrder = stats.perfectOrder ?? 96;

  // Box 6: most common on-time transport delivery, noting sectors below it
  const deliveryCommon = stats.deliveryCommon ?? 94;
  const deliveryExceptions = stats.deliveryExceptions ?? [{ label: 'Construcción', pct: 93 }];
  const deliveryExceptionText = deliveryExceptions.length > 0
    ? ` excepto ${deliveryExceptions.map((e) => `${e.label.toLowerCase()} (${e.pct}%)`).join(', ')}`
    : '';

  const handleOpenDialog = (title, content) => {
    setDialogContent({ title, content });
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
  };

  return (
    <Container sx={{ mt: 4 }}>
      {/* SECCIÓN DE TARJETAS */}
      <Box sx={{ mb: 6 }}>
      <Grid container columnSpacing={5} rowSpacing={5}>
          <Grid item xs={12}>
            <InfoCard>
              <Typography
                variant="h5"
                textAlign="center"
                sx={{ fontWeight: 'bold', color: '#1F3D58', width: '100%' }}
              >
                Top 3 Industrias - Carga Anual
              </Typography>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                textAlign="center"
                width="100%"
                px={3}
              >
                <Box textAlign="center">
                  <LocalMall sx={{ fontSize: 50, color: '#1F3D58', mb: 2, ml: '100px' }} />
                  <Typography sx={{ ml: '100px' }} variant="h6">Servicios logísticos</Typography>
                  <Typography sx={{ ml: '100px', fontWeight: 'bold' }} variant="body1">2.24 millones de pallets</Typography>
                </Box>

                <Divider orientation="vertical" flexItem sx={{ bgcolor: '#1F3D58', width: '2px', height: '100px' }} />

                <Box textAlign="center">
                  <LocalMall sx={{ fontSize: 50, color: '#1F3D58', mb: 2 }} />
                  <Typography variant="h6">Construcción</Typography>
                  <Typography sx={{ fontWeight: 'bold' }} variant="body1">1.87 millones toneladas</Typography>
                </Box>

                <Divider orientation="vertical" flexItem sx={{ bgcolor: '#1F3D58', width: '2px', height: '100px' }} />

                <Box textAlign="center">
                  <LocalMall sx={{ fontSize: 50, color: '#1F3D58', mb: 2, mr: '100px' }} />
                  <Typography sx={{ mr: '100px' }} variant="h6">Retail</Typography>
                  <Typography sx={{ mr: '100px', fontWeight: 'bold' }} variant="body1">48 millones de unidades</Typography>
                </Box>
              </Box>
            </InfoCard>
          </Grid>

          {/* TARJETAS INFORMATIVAS */}
          {[
            {
              icon: <InfoIcon sx={{ fontSize: 40, color: '#1F3D58', mr: 2 }} />,
              title: `${topShare}% ${topIndustry}`,
              text: 'La industria más representada en el estudio.',
              content: (
                <>
                  <DialogSectionTitle>Descripción</DialogSectionTitle>
                  <Typography paragraph>
                    La industria de {topIndustry.toLowerCase()} representa un {topShare}% del total de las organizaciones que comprenden este estudio.
                  </Typography>
                </>
              )
            },
            {
              icon: <AssessmentIcon sx={{ fontSize: 40, color: '#1F3D58', mr: 2 }} />,
              title: `${fillRate}% Fill rate`,
              text: `${fillSectors} son los sectores con mejor desempeño.`,
              content: (
                <>
                  <DialogSectionTitle>Descripción</DialogSectionTitle>
                  <Typography paragraph>
                    Los sectores de {fillSectors} alcanzaron un fill rate del {fillRate}%, lo que refleja una alta eficiencia en la gestión de inventarios.
                  </Typography>
                </>
              )
            },
            {
              icon: <PieChartIcon sx={{ fontSize: 40, color: '#1F3D58', mr: 2 }} />,
              title: `${billingOver250}% Facturación > 250 MDP`,
              text: 'Las empresas con mayor facturación.',
              content: (
                <>
                  <DialogSectionTitle>Descripción</DialogSectionTitle>
                  <Typography paragraph>
                    El {billingOver250}% de las empresas tienen facturación mayor a 250 millones de pesos.
                  </Typography>
                </>
              )
            },
            {
              icon: <PeopleIcon sx={{ fontSize: 40, color: '#1F3D58', mr: 2 }} />,
              title: 'Rotación del Personal',
              text: `${rotationSector} presenta la menor rotación de personal con ${rotationValue}%.`,
              content: (
                <>
                  <DialogSectionTitle>Descripción</DialogSectionTitle>
                  <Typography paragraph>
                    {rotationSector} es el sector con la menor rotación de personal logístico ({rotationValue}%), lo que refleja una retención contrastada respecto a otras industrias.
                  </Typography>
                </>
              )
            },
            {
              icon: <PeopleIcon sx={{ fontSize: 40, color: '#1F3D58', mr: 2 }} />,
              title: 'Eficiencia Operativa',
              text: `${perfectOrderSector} lidera con ${perfectOrder}% de órdenes de venta perfectas.`,
              content: (
                <>
                  <DialogSectionTitle>Descripción</DialogSectionTitle>
                  <Typography paragraph>
                    {perfectOrderSector} alcanza un {perfectOrder}% de órdenes de venta perfectas, indicador de precisión en entregas y cadena de suministro bien gestionada.
                  </Typography>
                </>
              )
            },
            {
              icon: <LocalShippingIcon sx={{ fontSize: 40, color: '#1F3D58', mr: 2 }} />,
              title: 'Eficiencia en entregas',
              text: `${deliveryCommon}% de entregas a tiempo del transporte en todas las industrias${deliveryExceptionText}.`,
              content: (
                <>
                  <DialogSectionTitle>Descripción</DialogSectionTitle>
                  <Typography paragraph>
                    Las entregas a tiempo del transporte al cliente alcanzan un {deliveryCommon}% en todas las industrias{deliveryExceptionText}. Un estándar clave en la satisfacción del cliente industrial.
                  </Typography>
                </>
              )
            }
          ].map(({ icon, title, text, content }, index) => (
            <Grid key={index} item xs={12} md={4} sx={{ minWidth: 0 }}>
              <InfoCard>
                <Box display="flex" alignItems="center">
                  {icon}
                  <Box>
                    <Typography variant="h6">{title}</Typography>
                    <Typography variant="body2">{text}</Typography>
                  </Box>
                </Box>
                <MoreInfoButton onClick={() => handleOpenDialog(title, (
                  <DialogContentWrapper>{content}</DialogContentWrapper>
                ))}>
                  Más información
                </MoreInfoButton>
              </InfoCard>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* AQUÍ PODRÍAS AGREGAR LA GRÁFICA */}
      {/* <Box>
        <Typography variant="h6" gutterBottom>Orden de venta perfecta por número de empleados</Typography>
        <ChartComponent />
      </Box> */}

      {/* Diálogo modal */}
      <Dialog open={open} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{dialogContent.title}</DialogTitle>
        <DialogContent>{dialogContent.content}</DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default InfoBoxes;
